/* eslint-disable jsx-a11y/label-has-associated-control,react/no-unused-prop-types */
import { Controller, useFormContext } from 'react-hook-form';

import { SquareColorCheckbox } from '@components/atoms/SquareColorCheckbox';
import { Popover } from '@components/organisms/Popover';

import { colorsDictionary } from '@utils/colorsDictionary';
import { translate } from '@utils/translations';

import { useMediaQuery } from '@hooks/useMediaQuery';
import { smallScreen } from '@styles/constants';

import { getTextureThumbnailUrl } from '@temp/core/utils';
import checkImg from 'images/check.svg';

import { MAX_SAMPLE_ITEMS } from '@components/organisms/AddToCartSection';

import { IAttributeFieldProps, IProductAttributesValues } from './types';
import * as S from './styles';

const CircleSwatchTypeItem: React.FC<
  IAttributeFieldProps & {
    i: number;
    value: IProductAttributesValues;
  }
> = ({ attribute, i, value }) => {
  const { register } = useFormContext();
  const [isSmallScreen] = useMediaQuery(`(max-width: ${smallScreen}px)`);

  return (
    <label key={value.id}>
      <input
        type="radio"
        name={attribute.attribute.slug}
        value={value.slug}
        {...register(attribute.attribute.slug)}
      />
      {isSmallScreen ? (
        <S.CircleSwatchItem
          style={{
            backgroundColor: colorsDictionary[value.slug],
            backgroundImage: `url(${getTextureThumbnailUrl(value.slug)})`,
            backgroundPositionY: ['top', 'center', 'bottom'][i % 3],
          }}
        >
          <S.CircleSwatchIcon src={checkImg} />
        </S.CircleSwatchItem>
      ) : (
        <Popover
          triggers={['hint']}
          placement="top"
          overlay={translate(value, 'name').replace('_', ' ').replace('-', ' ')}
        >
          <S.CircleSwatchItem
            style={{
              backgroundColor: colorsDictionary[value.slug],
              backgroundImage: `url(${getTextureThumbnailUrl(value.slug)})`,
              backgroundPositionY: ['top', 'center', 'bottom'][i % 3],
            }}
          >
            <S.CircleSwatchIcon src={checkImg} />
          </S.CircleSwatchItem>
        </Popover>
      )}
    </label>
  );
};

export const AttributeFieldCircleSwatch: React.FC<IAttributeFieldProps> = ({
  attribute,
}) => (
  <S.CircleSwatch>
    {attribute.values.map((value, i) => (
      <CircleSwatchTypeItem
        key={value.id}
        value={value}
        i={i}
        attribute={attribute}
      />
    ))}
  </S.CircleSwatch>
);

export const AttributeFieldSquareSwatch: React.FC<IAttributeFieldProps> = ({
  attribute,
  onOptionHover,
}) => {
  const { control } = useFormContext();

  return (
    <S.SquareSwatch>
      <Controller
        name={attribute.attribute.slug}
        control={control}
        render={({ field: { onChange, value } }) => {
          const maxSampleItemsReached = value.length === MAX_SAMPLE_ITEMS;
          return (
            <>
              {attribute.values.map((option) => {
                const checked = value.includes(option.slug);
                const disabled = !checked && maxSampleItemsReached;
                return (
                  <SquareColorCheckbox
                    onMouseEnter={() => onOptionHover(option)}
                    onMouseLeave={() => onOptionHover(null)}
                    key={option.id}
                    name={attribute.attribute.slug}
                    checked={checked}
                    onChange={() => {
                      const valueSet = new Set(value);
                      if (valueSet.has(option.slug)) {
                        valueSet.delete(option.slug);
                      } else {
                        valueSet.add(option.slug);
                      }
                      onChange([...valueSet]);
                    }}
                    value={option}
                    disabled={disabled}
                  />
                );
              })}
            </>
          );
        }}
      />
    </S.SquareSwatch>
  );
};

export const AttributeFieldSwatch: React.FC<IAttributeFieldProps> = ({
  squareOptions,
  onOptionHover,
  ...props
}) => {
  if (squareOptions) {
    return <AttributeFieldSquareSwatch {...{ ...props, onOptionHover }} />;
  }
  return <AttributeFieldCircleSwatch {...props} />;
};
